import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Info as InfoIcon,
  Language as GlobeIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import WebsitePageWrapper from './WebsitePageWrapper';
import { useNotification } from '../../../context/NotificationContext';

const WebsiteSEOPage = ({ userOrganizationId }) => {
  return (
    <WebsitePageWrapper
      title="SEO Settings"
      subtitle="Optimize how your website appears in search engine results"
      userOrganizationId={userOrganizationId}
    >
      {SEOContent}
    </WebsitePageWrapper>
  );
};

const SEOContent = ({ website, updateWebsite, isSaving }) => {
  const { getAccessTokenSilently } = useAuth0();
  const notification = useNotification();
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState(website?.seo?.title || '');
  const [description, setDescription] = useState(website?.seo?.description || '');
  const [ogImageUrl, setOgImageUrl] = useState(website?.seo?.og_image_url || '');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setTitle(website?.seo?.title || '');
    setDescription(website?.seo?.description || '');
    setOgImageUrl(website?.seo?.og_image_url || '');
  }, [website]);

  const handleSave = async () => {
    await updateWebsite({
      seo: {
        title,
        description,
        og_image_url: ogImageUrl,
      },
    });
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      notification.error('Image must be less than 5MB');
      return;
    }

    try {
      setIsUploading(true);
      const token = await getAccessTokenSilently();
      const fileName = `og-image-${Date.now()}-${file.name}`;
      const signedUrlResponse = await fetch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/s3/signed-url?type=store-asset&fileName=${encodeURIComponent(fileName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!signedUrlResponse.ok) throw new Error('Failed to get upload URL');

      const { signedUrl, fileUrl } = await signedUrlResponse.json();

      await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      setOgImageUrl(fileUrl);
      notification.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      notification.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const titleMaxChars = 60;
  const descMaxChars = 160;

  const previewUrl = website?.custom_domain_verified
    ? `https://${website.custom_domain}`
    : `https://${website?.slug || `${website?.subdomain_slug}-${website?.org_slug}`}.${process.env.REACT_APP_PLATFORM_DOMAIN || 'adgro.app'}`;

  return (
    <>
      <div className="website-page-content">
        {/* SEO Settings Form */}
        <div className="website-card">
          <div className="website-card-header">
            <h2 className="website-card-title">SEO Settings</h2>
            <p className="website-card-subtitle">
              Optimize how your website appears in search engine results
            </p>
          </div>

          <div className="website-card-body">
            {/* SEO Title */}
            <div className="website-form-group">
              <label className="website-form-label">SEO Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter your website title"
                className="website-form-input"
                maxLength={titleMaxChars}
              />
              <div className="website-form-helper">
                <span>{title.length}/{titleMaxChars} characters (recommended max)</span>
                {title.length > titleMaxChars && (
                  <span className="website-form-error">Title exceeds recommended length</span>
                )}
              </div>
            </div>

            {/* SEO Description */}
            <div className="website-form-group">
              <label className="website-form-label">SEO Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter a brief description of your website"
                className="website-form-textarea"
                rows={3}
                maxLength={descMaxChars}
              />
              <div className="website-form-helper">
                <span>{description.length}/{descMaxChars} characters (recommended max)</span>
                {description.length > descMaxChars && (
                  <span className="website-form-error">Description exceeds recommended length</span>
                )}
              </div>
            </div>

            {/* Social Share Image */}
            <div className="website-form-group">
              <label className="website-form-label">Social Share Image URL (optional)</label>
              <input
                type="url"
                value={ogImageUrl}
                onChange={(e) => setOgImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="website-form-input"
              />
              <p className="website-form-hint">
                Image shown when your site is shared on social media. Use your logo or a promotional image.
              </p>

              {/* Image Upload */}
              {!ogImageUrl ? (
                <label className="website-upload-area">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                    className="website-upload-input"
                    disabled={isUploading}
                  />
                  <div className="website-upload-content">
                    <UploadIcon className="website-upload-icon" />
                    <span className="website-upload-text">
                      {isUploading ? 'Uploading...' : 'Upload Social Share Image'}
                    </span>
                    <span className="website-upload-hint">Recommended size: 1200 x 630 pixels</span>
                  </div>
                </label>
              ) : (
                <div className="website-image-preview-container">
                  <div className="website-image-preview wide">
                    <img src={ogImageUrl} alt="Social share preview" />
                  </div>
                  <div className="website-image-preview-footer">
                    <span>Social share image uploaded</span>
                    <button
                      onClick={() => setOgImageUrl('')}
                      className="website-remove-btn"
                    >
                      <DeleteIcon style={{ fontSize: 16 }} />
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search Engine Preview */}
        <div className="website-card">
          <div className="website-card-header">
            <h2 className="website-card-title">Search Engine Preview</h2>
            <p className="website-card-subtitle">
              This is how your website might appear in search results
            </p>
          </div>

          <div className="website-card-body">
            <div className="website-seo-preview">
              <div className="website-seo-preview-url">
                <GlobeIcon style={{ fontSize: 14 }} />
                <span>{previewUrl}</span>
              </div>
              <h3 className="website-seo-preview-title">
                {title || 'Your Website Title'}
              </h3>
              <p className="website-seo-preview-desc">
                {description || 'Your website description will appear here. Make it compelling to attract more visitors from search engines.'}
              </p>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="website-card info-card">
          <div className="website-card-header">
            <div className="website-card-title-with-icon">
              <InfoIcon className="website-card-title-icon" />
              <h2 className="website-card-title">SEO Best Practices</h2>
            </div>
          </div>
          <div className="website-card-body">
            <ul className="website-info-list-card">
              <li>Keep your title under 60 characters for best display in search results</li>
              <li>Write a compelling description under 160 characters that includes key information</li>
              <li>Use your store name and location in the title for better local search visibility</li>
              <li>Update your social share image to make links to your site more engaging when shared</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Fixed Action Bar */}
      <div className="website-action-bar">
        <button className="action-btn primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save SEO Settings'}
        </button>
      </div>
    </>
  );
};

export default WebsiteSEOPage;
